import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import type { BudgetForecastEntry } from 'lumen/models';

import styles from './menu-entry.module.css';

export interface SettingsBudgetForecastMenuEntrySignature {
  Args: {
    archiveEntry: () => void;
    copyToExisting: () => void;
    deleteEntry: () => void;
    duplicateEntry: () => void;
    duplicateEntryAsOtherType: () => void;
    editEntryTitle: () => void;
    entry: BudgetForecastEntry;
    isDisabled: boolean;
    isLatestVersionSelected: boolean;
    lockEntry: () => void;
    viewVersionHistory: () => void;
  };
}

export default class SettingsBudgetForecastMenuEntry extends Component<SettingsBudgetForecastMenuEntrySignature> {
  @service declare intl: Services['intl'];

  get additionalActions(): {
    actionHandler: () => void;
    disabled?: boolean;
    disabledMessage?: string;
    hasAction: boolean;
    icon?: 'bin';
    id?: 'delete';
    label: string;
  }[] {
    const { entry } = this.args;

    const disabledMessage =
      entry.dataType === 'budget'
        ? this.intl.t('budget_forecast.budget.locked_message')
        : this.intl.t('budget_forecast.forecast.locked_message');

    const actions = [
      {
        label: this.intl.t('budget_forecast.edit_title'),
        hasAction: !entry.isMain && !entry.isArchived && entry.canEdit,
        disabled: entry.isLocked,
        disabledMessage: disabledMessage,
        actionHandler: this.args.editEntryTitle,
      },
      {
        label: this.intl.t('budget_forecast.view_history'),
        hasAction: true,
        actionHandler: this.args.viewVersionHistory,
      },
      {
        label: this.intl.t('budget_forecast.duplicate'),
        hasAction: this.args.isLatestVersionSelected,
        actionHandler: (): void => this.args.duplicateEntry(),
      },
      {
        label:
          entry.dataType === 'budget'
            ? this.intl.t('budget_forecast.duplicate_as_forecast')
            : this.intl.t('budget_forecast.duplicate_as_budget'),
        hasAction: this.args.isLatestVersionSelected,
        actionHandler: (): void => this.args.duplicateEntryAsOtherType(),
      },
      {
        label:
          entry.dataType === 'budget'
            ? this.intl.t('budget_forecast.copy_to_existing.budget')
            : this.intl.t('budget_forecast.copy_to_existing.forecast'),
        hasAction: this.args.isLatestVersionSelected,
        actionHandler: (): void => this.args.copyToExisting(),
      },
      {
        label: this.intl.t('budget_forecast.archive'),
        hasAction:
          !entry.isMain &&
          !entry.isArchived &&
          entry.canEdit &&
          this.args.isLatestVersionSelected,
        disabled: entry.isLocked,
        disabledMessage: disabledMessage,
        actionHandler: this.args.archiveEntry,
      },
      {
        label: this.intl.t('budget_forecast.lock'),
        hasAction:
          !entry.isArchived &&
          !entry.isLocked &&
          entry.canEdit &&
          this.args.isLatestVersionSelected,
        actionHandler: this.args.lockEntry,
      },
      {
        id: 'delete' as const,
        label: this.intl.t('general.delete'),
        icon: 'bin' as const,
        hasAction: !entry.isMain && entry.canEdit,
        disabled: entry.isLocked,
        disabledMessage: disabledMessage,
        actionHandler: this.args.deleteEntry,
      },
    ];

    return actions.filter((action) => action.hasAction);
  }

  get additionalActionsHasDelete(): boolean {
    return this.additionalActions.some(
      (action) => action.id === 'delete' && !action.disabled,
    );
  }

  @action
  onSelectAdditionalAction(
    option: (typeof this.additionalActions)[number],
  ): void {
    option.actionHandler();
  }

  <template>
    {{#if this.additionalActions.length}}
      <PrismComboSelectInput
        @onChange={{this.onSelectAdditionalAction}}
        @options={{this.additionalActions}}
        @unsafe_dropdownClass={{if
          this.additionalActionsHasDelete
          styles.dropdown--with-delete
        }}
        @unsafe_hasDropdownMaxHeight={{false}}
        @unsafe_triggerComponent={{component
          GeneralDropdownsTriggersTriggerDropdownIconButton
          buttonType="secondary"
          disabledMessage=(if
            (eq @entry.dataType "budget")
            (t "budget_forecast.budget.additional_actions_disabled")
            (t "budget_forecast.forecast.additional_actions_disabled")
          )
          icon="kebab"
          isDisabled=@isDisabled
        }}
        data-test-action-menu
      />
    {{/if}}
  </template>
}
